import React, { useCallback, useRef, useState } from 'react';
import { css } from '@compiled/react';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PlacementFetchProvider } from '@confluence/confluence-post-office-provider/entry-points/placement-fetch-provider.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { UNSAFE_isExperimentEnabledNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useCheckIsLocaleEnglish } from '@confluence/onboarding-helpers/entry-points/hooks/useCheckIsLocaleEnglish.ts';

import {
	ReviewContextualNudgeEligibility,
	type ReviewContextualNudgeContext,
} from './ReviewContextualNudgeEligibility.tsx';

// Wraps the button without adding a box, so it only observes clicks.
const targetStyles = css({ display: 'contents' });

// `done` once the nudge has left the screen, so it isn't rendered again.
type NudgeStatus = 'idle' | 'visible' | 'done';

export type ReviewButtonNudgeContext = ReviewContextualNudgeContext;

type ReviewButtonNudgeProps = {
	/** Receives whether a nudge is on screen, so the button can drop its tooltip rather than sit under the nudge. */
	children: (state: { isNudgeVisible: boolean }) => React.ReactElement;
	isVisible: boolean;
	onGetSuggestions?: () => void;
	context?: ReviewButtonNudgeContext;
};

/**
 * Anchors the Post Office spotlight nudge to the Review suggestions button: a contextual nudge for
 * eligible pages. It only shows for owners and contributors of pages that weren't created with
 * Rovo. The button stays in a stable tree so it never remounts when a message arrives, and the
 * nudge is optional UI that fails closed.
 *
 * The nudge is shown at most once while this button stays mounted: once it has left the screen it
 * isn't rendered again.
 */
export const ReviewButtonNudge = ({
	children,
	isVisible,
	onGetSuggestions,
	context,
}: ReviewButtonNudgeProps): React.JSX.Element => {
	const [status, setStatus] = useState<NudgeStatus>('idle');
	const targetClickListenersRef = useRef(new Set<() => void>());
	const isEnglishLocale = useCheckIsLocaleEnglish();

	const subscribeToTargetClick = useCallback((listener: () => void) => {
		const listeners = targetClickListenersRef.current;
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	}, []);

	const handleShown = useCallback(() => setStatus('visible'), []);
	const handleHidden = useCallback(() => setStatus('done'), []);

	// Clicking Review settles the nudge.
	const handleTargetClick = useCallback(() => {
		targetClickListenersRef.current.forEach((listener) => listener());
	}, []);

	// Post Office records the narrow experiment exposure when it actually shows a message, so this
	// only decides whether to ask for one and must not record an exposure of its own. The copy is
	// English only until the translations are ready, which the i18n gate signals.
	// Note that since exposures are on Post Office side, the nudge may not be shown evenly across cohorts
	// while its English only
	if (
		!isVisible ||
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		(!isEnglishLocale && !fg('platform_editor_ai_suggested_edits_nudges_i18n')) ||
		!UNSAFE_isExperimentEnabledNoExposure('platform_editor_ai_suggested_edits_nudges')
	) {
		return children({ isNudgeVisible: false });
	}

	return (
		<PopoverProvider>
			<PopoverTarget>
				<span css={targetStyles} onClickCapture={handleTargetClick}>
					{children({ isNudgeVisible: status === 'visible' })}
				</span>
			</PopoverTarget>
			<TransparentErrorBoundary attribution={Attribution.CC_EDITOR_AI}>
				<PlacementFetchProvider>
					{status !== 'done' && (
						<ReviewContextualNudgeEligibility
							context={context}
							onShown={handleShown}
							onHidden={handleHidden}
							onGetSuggestions={onGetSuggestions}
							subscribeToTargetClick={subscribeToTargetClick}
						/>
					)}
				</PlacementFetchProvider>
			</TransparentErrorBoundary>
		</PopoverProvider>
	);
};
